/**
 * Pipeline tab grouping (loan_and_auth_endpoints.md §3–§4).
 *
 * Pre-Close / Closing / Post-Close are NOT separate endpoints — they are
 * client-side slices of the SAME pipeline payload
 * (`/underwriting/underwriting_pipeline`). Fetch once, filter in memory.
 *
 * Membership is decided by each tab's `match(deal)` predicate so that every deal
 * lands somewhere and nothing is silently dropped:
 *
 *   - Closing    → loanStatus "Clear To Close" (regardless of pipeline bucket).
 *   - Post-Close → pipeline "Closed" OR loanStatus "Closed".
 *   - Pre-Close  → EVERY deal in the payload. It is the full working board, not
 *                  a slice: Clear To Close, Closed, Inactive and Dead loans all
 *                  show up there alongside the in-flight ones, and the status
 *                  chips / column filters are how you narrow it. Pre-Close
 *                  therefore overlaps Closing and Post-Close by design — those
 *                  two are the focused views, Pre-Close is "show me everything".
 *
 * Why a predicate instead of a per-tab status allow-list: the old allow-list
 * dropped any loan whose `loanStatus` wasn't explicitly enumerated (e.g.
 * "Prospect"), so the pipeline showed far fewer loans than the API returned.
 * The catch-all Pre-Close rule keeps new/edge statuses visible.
 *
 * `statuses` (below) is now ONLY the set of quick-filter chips surfaced in the
 * toolbar — it does NOT gate membership.
 *
 * Lock Requests has NO backend endpoint yet — keep it disabled / behind a flag.
 */

// Milestones that have advanced past the in-flight stages. They give the
// Closing / Post-Close tabs their focused slices; Pre-Close still lists them.
const CLOSING_STATUS = "Clear To Close";
const POST_CLOSE_STATUS = "Closed";

export const PIPELINE_TABS = {
  PRE_CLOSE: {
    key: "PRE_CLOSE",
    label: "Pre-Close",
    slug: "pre-close",
    // Status chips only (not a membership gate). "All" + the column filter
    // still expose every status that lands in this tab.
    statuses: [
      "Prospect",
      "Terms Accepted",
      "Processing",
      "Underwriting",
      "Condition Clearance",
      "Final Review",
      CLOSING_STATUS,
      POST_CLOSE_STATUS,
      "Inactive",
      "Dead",
    ],
    // Everything the pipeline endpoint returns — no exclusions.
    //
    // This tab is the full board, so nothing can silently vanish from it: deals
    // that reached Clear To Close, ones marked Closed, and Inactive / Dead ones
    // all stay listed (each has its own chip above). Filtering by `pipeline`
    // used to drop any deal whose bucket wasn't "Underwriting", and excluding
    // CLOSING_STATUS hid every Clear To Close loan.
    match: (d) => d != null,
  },
  CLOSING: {
    key: "CLOSING",
    label: "Closing",
    slug: "closing",
    statuses: [CLOSING_STATUS], // subStatus: Docs Out / Docs Back
    match: (d) => d?.loanStatus === CLOSING_STATUS,
  },
  POST_CLOSE: {
    key: "POST_CLOSE",
    label: "Post Close",
    slug: "post-close",
    pipelines: ["Closed"],
    statuses: [POST_CLOSE_STATUS], // funded; incl. post-close conditions
    match: (d) => d?.pipeline === "Closed" || d?.loanStatus === POST_CLOSE_STATUS,
  },
  LOCK_REQUESTS: {
    key: "LOCK_REQUESTS",
    label: "Lock Requests",
    slug: "lock-requests",
    statuses: [], // ⚠️ no backend endpoint yet — keep disabled
    match: () => false,
  },
};

/**
 * Filter a fetched pipeline down to the deals belonging to one tab, using that
 * tab's `match` predicate. Returns [] for unknown tabs or non-array input.
 */
export const dealsForTab = (deals, tabKey) => {
  const tab = PIPELINE_TABS[tabKey];
  if (!tab || typeof tab.match !== "function" || !Array.isArray(deals)) {
    return [];
  }
  return deals.filter((d) => tab.match(d));
};

/** Per-tab deal counts (for tab badges). */
export const tabCounts = (deals) =>
  Object.fromEntries(
    Object.keys(PIPELINE_TABS).map((k) => [k, dealsForTab(deals, k).length]),
  );

export default PIPELINE_TABS;
